<template>
  <div class="alternativas-introduccion m-x-3-lg m-x-2-md m-y-2-md">
    <h1 class="titulo">Introducción</h1>

    <LeerMasMenos>
      <template v-slot:texto-corto>
        <p class="texto-corto">
          Las arvenses son plantas herbáceas propias de la sucesión temprana que
          sigue a una fuerte perturbación. Para ser exitosas en estas
          condiciones volátiles, han evolucionado con características
          sorprendentes: pueden tener bancos de semillas que se caracterizan por
          ser grandes, longevos y recalcitrantes; además, pueden crecer,
          florecer y fructificar con rapidez, dispersarse ampliamente, ser muy
          competitivas, adaptables e incluso pueden llegar a ser invasoras.
          Estas plantas son las ancestras de muchísimos cultivos anuales. Han
          coevolucionado por más de 11,000 años con nosotros y sus cultivos
          mimetizan la fisionomía y el ciclo de vida del cultivo.
          <span>
            Debido a sus características como plantas pioneras, voluntarias,
            escapistas y cosmopolitas, no pueden (ni deben) ser erradicadas de
            los sistemas agrícolas actuales y futuros. Cuando no son debidamente
            manejadas pueden mermar el rendimiento de los cultivos pero, con
            prácticas de manejo integrado y estrategias culturales apropiadas
            desarrolladas por los pobladores locales, pueden coexistir sin mayor
            problema con éstos y cumplir importantes funciones ecológicas,
            económicas y culturales en una gran diversidad de agroecosistemas.
          </span>
        </p>
      </template>

      <template v-slot:texto-largo>
        <p>
          La forma en que las y los agricultores perciben a las arvenses es un
          factor de gran relevancia para determinar el manejo o control que se
          les puede dar a estas especies. Para algunos, se trata de plantas con
          un gran potencial, que deben ser aprovechadas como alimento
          (quelites), medicina, ornamento, forraje, abono, material de
          construcción, para la producción casera de bioinsecticidas o como
          elemento ceremonial. Otra forma de percibirlas es como recursos del
          agroecosistema que deben ser valorados y conservados, pues contribuyen
          a su funcionamiento al reciclar nutrientes, mantener la humedad,
          evitar la erosión del suelo o servir como hábitat para distintos
          organismos que realizan funciones centrales para los propios cultivos,
          tal es el caso de las abejas y sus servicios de polinización. Por
          último, también existe un grupo de agricultores volcados al modelo
          agroindustrial o influidos en algún sentido por éste, para los que las
          arvenses sólo son “plagas”, “malezas” o “malas yerbas” que deben ser
          eliminadas a toda costa, en aras de maximizar, a corto plazo, el
          rendimiento del producto comercial de interés y las ganancias del
          capital invertido, sin asumir las externalidades generadas.
        </p>
        <p>
          En México existe una larga historia de manejo de las arvenses.
          Comienza con el enorme legado agrícola mesoamericano, en el que las y
          los campesinos han aprovechado y controlado a las arvenses sin usar
          herbicidas durante miles de años. La agricultura mesoamericana se
          caracteriza por el uso de policultivos (como la milpa) en los que
          coexisten las plantas cultivadas con las arvenses, que se consideran
          importantes recursos del agroecosistema. También existe un fuerte
          movimiento agroecológico mexicano, pionero a nivel mundial en muchas
          estrategias de manejo de los agroecosistemas. Otros espacios en los
          que se trabajan estrategias de manejo ecológicas e integrales de las
          arvenses son: la agricultura orgánica nacional, los desarrollos
          tecnológicos comprometidos, las innovaciones abiertas y disruptivas y
          los programas de Sader, Bienestar y Semarnat.
        </p>
        <p>
          Diversos estudios han demostrado que se puede hacer un manejo
          integrado ecológico de las arvenses, distinto al de las herramientas
          promovidas por el modelo agroindustrial, y que éste puede mantener el
          rendimiento y la eficiencia del sistema agrícola en cuestión. Dicho
          manejo está compuesto por diversas prácticas que afectan diferentes
          momentos del ciclo de vida de las arvenses y permite identificar los
          niveles de tolerancia óptimos de estas plantas pioneras en los
          sistemas productivos, para fomentar que cumplan funciones biológicas y
          ecológicas fundamentales para los agroecosistemas, sin comprometer las
          cosechas de los cultivos de interés agrícola. Algunas de estas
          prácticas son: deshierbar con machete, roturar y desbrozar con
          maquinaria ligera, hacer barreras de coberteras vivas, asperjar
          bioherbicidas y retardantes, construir coberteras de residuos, hacer
          siembra temprana, fomentar sombra de cultivos asociados, fomentar
          sombra de cultivo denso y cosechar forrajes.
        </p>
        <p>
          En los últimos 70 años, el uso del glifosato ha crecido de manera
          exponencial hasta convertirse en el herbicida más empleado a nivel
          mundial. Tan sólo entre 1996 y 2014, se multiplicaron por quince las
          toneladas de glifosato que se aplicaban globalmente (Benbrook, 2016).
          En México, en el año 2018 se usaron 17,395,975 kg de glifosato
          formulado y 1,323,401 kg de glifosato técnico. Este aumento del uso
          del glifosato no ha logrado desplazar otras estrategias de manejo de
          arvenses, pero sí se ha vuelto hegemónica. Una de las razones por las
          que el uso agrícola del glifosato está tan presente en México, para la
          mediana y gran agricultura, es porque representó el recurso ideal
          para instaurar un modelo de ganancias extraordinarias en cada eslabón
          de la cadena de insumos-productos-consumo, sin reparar en sus enormes
          externalidades. Para la agricultura de autoabasto y orientada al
          mercado local, el glifosato fue promovido agresivamente por las
          corporaciones, aprovechando las políticas que propiciaron el éxodo
          rural y la consecuente escasez de mano de obra. Finalmente, la noción
          de que el glifosato es la mejor y única opción para manejar las
          arvenses y hacer rentable la producción ha permeado culturalmente en
          muchos sectores de productores, del gobierno y de las instituciones de
          enseñanza agronómica convencionales.
        </p>
        <p>
          A partir del 31 de diciembre del 2020, con la entrada en vigor del
          Decreto Presidencial para la sustitución gradual del glifosato por
          alternativas sostenibles, el gobierno de México asumió la
          responsabilidad de eliminar el uso de dicha sustancia agrotóxica. Este
          Decreto establece una serie de atribuciones y responsabilidades que
          habrán de encabezar las dependencias y entidades que integran la
          Administración Pública Federal (APF), a fin de asegurar la transición
          hacia agriculturas libres de glifosato. En el ámbito de su
          competencia, una de las tareas centrales que se le ha encomendado al
          Consejo Nacional de Humanidades, Ciencias y Tecnologías de México (Conahcyt) es
          coordinar, promover y apoyar las investigaciones científicas,
          desarrollos tecnológicos e innovaciones que permitan sustentar y
          proponer alternativas sostenibles y culturalmente adecuadas, además
          que mantengan la producción y resulten seguras para la salud humana,
          la diversidad biocultural del país y el ambiente.
        </p>
        <p>
          Es posible manejar y controlar las plantas arvenses en las distintas
          escalas y cultivos de interés existentes en México, por medio de
          estrategias y prácticas agrícolas integrales que no causen daños a la
          salud humana, al ambiente, ni a la producción de alimentos. Esta
          página busca visibilizar esas estrategias y prácticas de manejo de las
          arvenses que ya existen o que están en desarrollo. Aquí se recopilan
          investigaciones científicas, manuales, pláticas, proyectos y
          experiencias teóricas y exitosas que permiten acercarse al manejo
          integrado de las arvenses y recuperar así el uso que ancestralmente
          han tenido.
        </p>
      </template>
    </LeerMasMenos>

    <div class="gacetas-meia m-t-2">
      <h2 class="subtitulo">Gacetas MEIA Conahcyt</h2>
      <div class="contenedor-documentos">
        <DownloadFile
          v-for="(material, i) in materiales"
          :key="`material_${i}`"
          :file="`${rutaDatos}/${material.archivo}`"
          type="application/pdf"
          :name="material.nombre"
          :downloadname="
            `${material.archivo}`.toLowerCase().replaceAll(' ', '-')
          "
          :cover="`${rutaDatos}/${material.imagen}`"
        />
      </div>
    </div>
    <div class="gacetas-meia m-t-2">
      <h2 class="subtitulo">Acciones de gobierno</h2>
      <div class="contenedor-documentos">
        <DownloadFile
          v-for="(infografia, i) in infografias"
          :key="`infografia_${i}`"
          :file="`${rutaDatos}/${infografia.archivo}`"
          type="application/pdf"
          :name="infografia.nombre"
          :downloadname="
            `${infografia.archivo}`.toLowerCase().replaceAll(' ', '-')
          "
          :cover="`${rutaDatos}/${infografia.imagen}`"
        />
      </div>
    </div>
    <div class="secciones-principales m-t-2">
      <h2 class="subtitulo">Visita nuestras secciones principales</h2>
      <div class="secciones-principales-lista contenedor-documentos">
        <router-link
          v-for="(seccion, i) in seccionesPrincipales"
          :key="`seccion${i}`"
          :to="`/alternativas/${seccion.ruta}`"
          class="tarjeta-img-simple m-b-1"
        >
          <img
            :src="require(`@/assets/img/alternativas/${seccion.imagen}`)"
            alt=""
          />
          <div class="tarjeta-img-simple-pie">{{ seccion.texto }}</div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import LeerMasMenos from "@/components/base/LeerMasMenos.vue";

import DownloadFile from "@/components/DownloadFile.vue";

export default {
  data() {
    return {
      seccionesPrincipales: [
        {
          ruta: "principios-manejo-ecologico-arvenses-articulos",
          imagen: "img_intro_001.jpg",
          texto: "Principios del manejo Ecológico Integral de Arvenses (MEIA)",
        },
        {
          ruta: "practicas-especiales-articulos",
          imagen: "img_intro_002.jpg",
          texto: "Prácticas específicas del MEIA",
        },
        {
          ruta: "enlaces",
          imagen: "img_intro_003.jpg",
          texto: "Experiencias exitosas de transición al MEIA",
        },
      ],
      rutaDatos: "descargables/alternativas/materiales",
      materiales: [
        {
          archivo: "meia_compilado_gacetas.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Compilado de gacetas 1 a 27.",
          imagen: "meia_compilado_gacetas.png",
        },
        {
          archivo: "meia_27_doce-practicas-de-manejo-ecologico-integral-de-arvenses-para-México.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Doce prácticas de Manejo Ecológico Integral de Arvenses para México.",
          imagen: "meia_27_doce-practicas-de-manejo-ecologico-integral-de-arvenses-para-México.png",
        },
        {
          archivo: "meia_26_ensenanza-de-meia.pdf", 
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. La enseñanza del manejo de arvenses en México II.",
          imagen: "meia_26_ensenanza-de-meia.png",
        },
        {
          archivo: "meia_25_ensenanza-de-meia.pdf", 
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. La enseñanza del manejo de arvenses en México.",
          imagen: "meia_25_ensenanza-de-meia.png",
        },
        {
          archivo: "meia_24_manejo-de-arvenses-en-la-parcela.pdf", 
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Los arvenses tienen efectos ecológicos positivos en las parcelas.",
          imagen: "meia_24_manejo-de-arvenses-en-la-parcela.png",
        },
        {
          archivo: "meia_23_biologia-y-ecologia-de-las-arvenses_20230606.pdf", 
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Historia natural de las arvenses. Glosario botánico ilustrado para estructuras comunes en las arvenses.",
          imagen: "meia_23_biologia-y-ecologia-de-las-arvenses_20230606.png",
        },
        {
          archivo: "meia_22_decreto-presidencial-sobre-la-eliminacion-gradual-del-glifosato-del-2020-al-2023.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Segunda temporada de la gaceta MEIA. Se consolida la eliminación gradual del glifosato en México con el segundo Decreto Presidencial.",
          imagen: "meia_22_decreto-presidencial-sobre-la-eliminacion-gradual-del-glifosato-del-2020-al-2023.png",
        },
        {
          archivo: "meia_21_mas-alla-del-control-de-arvenses-otros-efectos-ecologicos-y-economicos-de-las-practicas.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Más allá del control de arvenses.",
          imagen: "meia_21_mas-alla-del-control-de-arvenses-otros-efectos-ecologicos-y-economicos-de-las-practicas.jpg",
        },
        {
          archivo: "meia_20_coberturas-plasticas.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Coberturas plásticas.",
          imagen: "meia_20_coberturas-plasticas.png",
        },
        {
          archivo: "meia_19_bioherbicidas.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Bioherbicidas.",
          imagen: "meia_19_bioherbicidas.png",
        },
        {
          archivo: "meia_18_pastoreo.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Pastoreo de Arvenses.",
          imagen: "meia_18_pastoreo.png",
        },
        {
          archivo: "meia_17_rotacion_de_cultivos.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Rotación de cultivos.",
          imagen: "MEIA_17_Rotacion_de_cultivos.png",
        },
        {
          archivo: "meia_16_policultivo_agroforestal.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Policultivos Agroforestales.",
          imagen: "MEIA_16_Policultivo_agroforestal.png",
        },
        {
          archivo: "MEIA_15_Policultivo_anua.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Policultivos Anuales.",
          imagen: "MEIA_15_Policultivo_anua.png",
        },
        {
          archivo: "MEIA_14_Siembra_cercana_20220420_logogob.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Siembra cercana.",
          imagen: "MEIA_14_Siembra_cercana_20220420_final.png",
        },
        {
          archivo: "MEIA_13_Motocultor_20220408.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Monocultor.",
          imagen: "Portada-MEIA-13-2022.jpeg",
        },
        {
          archivo: "MEIA_12_desbrozadora_y_machete.pdf",
          nombre:
            "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Desbrozadora.",
          imagen: "MEIA_12_desbrozadora_y_machete.png",
        },
        {
          archivo: "MEIA_11_falsa siembra_20220311_2.pdf",
          nombre:
            "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Falsa siembra.",
          imagen: "MEIA_11_falsa_siembra_portada.jpg",
        },
        {
          archivo: "MEIA_10_coberturas vivas_01032022_2.pdf",
          nombre:
            "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Coberturas vivas.",
          imagen: "coberturas-vivas.png",
        },
        {
          archivo: "MEIA_09_coberturas muertas_12022022.pdf",
          nombre:
            "Gaceta MEIA Conahcyt: Manos a la obra. Como aplicar las prácticas MEIA. Coberturas vegetales.",
          imagen: "MEIA_09_coberturas.jpg",
        },
        {
          archivo: "MEIA_08_herbicidas naturales_240120223.pdf",
          nombre:
            "Gaceta MEIA Conahcyt: Control de arvenses con herbicidas naturales",
          imagen: "MEIA_08_herbicidas.jpg",
        },
        {
          archivo: "MEIA_07_control_biologico_10012022.pdf",
          nombre:
            "Gaceta MEIA Conahcyt: Control de arvenses con control biológico",
          imagen: "MEIA_07_controlBiologico.jpg",
        },
        {
          archivo: "MEIA_06_coberturas_16122021_02.pdf",
          nombre: "Gaceta MEIA Conahcyt: Control de arvenses con coberturas",
          imagen: "img_intro_006.jpg",
        },
        {
          archivo: "MEIA_05_Manejo mecanico_03122021.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manejo mecánico",
          imagen: "img_PMEIAII_manejoMecanico (1).jpg",
        },
        {
          archivo: "MEIA_04_Manejo_fisico.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manejo físico",
          imagen: "img_PMEIAII_manejoFisico (1).jpg",
        },
        {
          archivo: "MEIA_03_Manejo_cultural.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manejo cultural",
          imagen: "img_PMEIAII_manejoCultural.jpg",
        },
        {
          archivo: "MEIA_02_Manejo_preventivo.pdf",
          nombre: "Gaceta MEIA Conahcyt: Manejo preventivo",
          imagen: "img_PMEIAII_manejoPreventivo.jpg",
        },
        {
          archivo: "MEIA_01_Presentacion.pdf",
          nombre: "Gaceta MEIA Conahcyt: Presentación",
          imagen: "img_PMEIAII_Presentacion.jpg",
        },
      ],
      infografias: [
        {
          archivo: "infografia_acciones_gob_glifosato.pdf",
          nombre: "Control de arvenses sin glifosato",
          imagen: "img_intro_info_01.jpg",
        },
      ],
    };
  },
  components: {
    LeerMasMenos,
    DownloadFile,
  },
  mounted() {
    this.$store.commit("setTemaAlternativa", "");
    this.$store.commit("setSubtemaAlternativa", "Introducción");
  },
};
</script>

<style lang="scss">
.dai-leer-mas-menos {
  &.abierto {
    .texto-corto {
      span {
        display: inline;
      }
    }
    .texto-largo {
      max-height: 1500px !important;
    }
  }
}

.tarjeta-img-simple {
  box-shadow: 0px 4px 10px #0000001f;
  border-radius: 10px;
  overflow: hidden;
  display: block;
  img {
    width: 100%;
  }
  &-pie {
    padding: 16px;
  }
}

.alternativas-introduccion {
  .texto-corto {
    span {
      display: none;
    }
  }
}
</style>
